import { DS_HEIGHT, PRIMITIVES } from '@calm/evexia-design-tokens';
import resourceMessages from '@rhg/components/resources/messages';
import crisisMessages from '@rhg/lang/messages/crisis';
import {
    Box,
    Button,
    Divider,
    Stack,
    Theme,
    Typography,
} from '@rhg/libs/material';
import { crisisReferralId } from '@rhg/utils/constants';
import { useCallback, useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { CrisisDialog } from '../crisis-dialog';
import { SheetDialog } from '../sheet-dialog';
import { DialogActions, MobileAppDownloadActions } from './actions';
import { ActionDataProps } from './types';

const styleDialog = {
    pb: 4,
};

const styleLearnMoreButton = {
    my: 0.5,
    borderRadius: `${PRIMITIVES.radius._4}px`,
};

const styleDivider = {
    my: 4,
    px: 4,
    width: (theme: Theme) =>
        `calc(100% - ${2 * theme.semantics.Spacing[16]}px)`,
};

const styleContentTitle = {
    px: 6,
    whiteSpace: 'pre-line',
};

const styleContentTitleLarge = {
    pb: 4,
    ...styleContentTitle,
};

const styleContentTitleSmall = {
    py: 2,
    ...styleContentTitle,
};

const styleActionsWrapper = {
    py: 2,
    px: 4,
    width: '100%',
};

interface ActionDialogProps {
    variant?: 'referral' | 'resource';
    actionData: ActionDataProps;
    onClose: () => void;
}

export function ActionDialog({
    variant = 'referral',
    actionData,
    onClose,
}: ActionDialogProps) {
    const { formatMessage } = useIntl();
    const titleId = useId();
    const [isCrisisDialogOpen, setIsCrisisDialogOpen] = useState(false);
    const isDetailed = !actionData.description;

    const isCrisisDialog = useMemo(
        () => actionData.id === crisisReferralId,
        [actionData?.id]
    );

    const handleCrisisDialogOpen = useCallback(() => {
        setIsCrisisDialogOpen(true);
        onClose();
    }, [onClose]);

    const handleCrisisDialogClose = useCallback(
        () => setIsCrisisDialogOpen(false),
        []
    );

    return (
        <>
            <CrisisDialog
                isOpen={isCrisisDialogOpen}
                onClose={handleCrisisDialogClose}
            />
            {!!actionData.id && (
                <SheetDialog
                    trackDialog={
                        isCrisisDialog
                            ? 'CrisisReferralDialog'
                            : actionData.trackDialog
                            ? actionData.trackDialog
                            : 'NotMapped'
                    }
                    trackProps={{
                        ResourceName: actionData.title,
                        ResourceCategory: actionData.category,
                        ResourceTab: actionData.selectedTab,
                    }}
                    aria-labelledby={titleId}
                    onClose={onClose}
                    disableMobileFullscreen={false}
                    imageSrc={actionData.logoUrl}
                    imageAlt={actionData.logoName || ''}
                    imageSize={DS_HEIGHT.BANNER_SIZE_SMALL}
                    content={
                        <Box sx={styleDialog}>
                            <Stack alignItems="center">
                                {Boolean(actionData.category) && (
                                    <Typography
                                        variant="caption"
                                        color="text.sub"
                                        textAlign="center"
                                    >
                                        {actionData.category}
                                    </Typography>
                                )}
                                <Typography
                                    id={titleId}
                                    pt={actionData.category ? 0.5 : 4}
                                    variant="h6"
                                    color="text.body"
                                    textAlign="center"
                                >
                                    {actionData.title}
                                </Typography>
                                {Boolean(actionData.sponsor) && (
                                    <Typography
                                        variant="caption"
                                        color="text.sub"
                                        textAlign="center"
                                        pt={2}
                                    >
                                        {formatMessage(
                                            resourceMessages[
                                                'resources.action_sheet.description'
                                            ],
                                            {
                                                sponsor: actionData.sponsor,
                                            }
                                        )}
                                    </Typography>
                                )}
                                {Boolean(actionData.hasLearnMore) && (
                                    <Button
                                        skipTracking
                                        size="small"
                                        sx={styleLearnMoreButton}
                                        onClick={handleCrisisDialogOpen}
                                    >
                                        {formatMessage(
                                            crisisMessages[
                                                'crisis.crisis_card.learn_more'
                                            ]
                                        )}
                                    </Button>
                                )}
                                {!actionData.recommendation ||
                                (actionData.recommendation &&
                                    (isDetailed || actionData.hasLearnMore)) ? (
                                    <Divider sx={styleDivider} />
                                ) : undefined}
                                <Stack mx={4}>
                                    {Boolean(actionData.header) && (
                                        <Typography
                                            variant="body1Demi"
                                            color="text.body"
                                            textAlign="center"
                                            pt={4}
                                        >
                                            {actionData.header}
                                        </Typography>
                                    )}
                                    {Boolean(actionData.description) && (
                                        <Typography
                                            sx={
                                                variant === 'referral'
                                                    ? isCrisisDialog
                                                        ? styleContentTitleLarge
                                                        : styleContentTitleSmall
                                                    : styleContentTitleLarge
                                            }
                                            variant="body1"
                                            color="text.body"
                                            textAlign="center"
                                        >
                                            {actionData.description}
                                        </Typography>
                                    )}
                                    {Boolean(actionData.additionalCopy) && (
                                        <Typography
                                            variant="body1"
                                            color="text.body"
                                            textAlign="center"
                                            pb={4}
                                        >
                                            {actionData.additionalCopy}
                                        </Typography>
                                    )}
                                    {Boolean(actionData.buttons) && (
                                        <DialogActions
                                            onClose={onClose}
                                            variant={variant}
                                            wrapperSx={styleActionsWrapper}
                                            actionData={actionData}
                                        />
                                    )}
                                </Stack>
                                {Boolean(actionData.hasMobileLinks) && (
                                    <Box
                                        {...(isDetailed
                                            ? {
                                                  sx: {
                                                      mt: 4,
                                                  },
                                              }
                                            : {})}
                                    >
                                        <MobileAppDownloadActions
                                            iosLink={actionData.iosLink}
                                            androidLink={actionData.androidLink}
                                        />
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    }
                />
            )}
        </>
    );
}
